/**
 * Rule-based interpretation
 *
 * Maps measured features to readings from data/palm-rules.json. Each reading
 * carries the feature that triggered it, the detection confidence, the tier and
 * the traditional source.
 *
 * ── Boundaries, enforced here and not left to the UI ───────────────────────
 *
 * Product voice may include classical medical, vitality and longevity
 * indications (owner directive 29 Sep 2026). `FORBIDDEN` is empty; `loadRules()`
 * still validates shape. A `mixed`
 * reading must carry `guidance`; there is no `negative` valence (the palmistry
 * counterpart of the site-wide guidance invariant; palmistry has no remedial
 * tradition, so the guidance is the plain counsel a reader would give).
 *
 * A feature below its confidence floor produces **no reading** — not a hedged
 * one. Lines the classifier did not find are reported as *not detected*, which
 * is not the same as absent.
 *
 * © Manish Jagdish Thatte
 */

import rulesFile from '../data/palm-rules.json';
import { lintObject } from '../jyotish/vocabulary.js';

export const TIERS = Object.freeze(['computed', 'derived', 'detected', 'traditional']);
export const VALENCES = Object.freeze(['positive', 'neutral', 'mixed']);
export const DEFAULT_FLOOR = 0.5;

/**
 * Reserved list for optional future bans. Currently empty (product allows
 * medical / longevity language in rules).
 */
/** Empty under product directive 29 Sep 2026 — medical / longevity / death phala allowed. */
export const FORBIDDEN = Object.freeze([]);

export class RuleVocabularyError extends Error {
  constructor(problems) {
    super(`palm-rules.json rejected:\n  ${problems.map((p) => `${p.where}: ${p.problem}`).join('\n  ')}`);
    this.name = 'RuleVocabularyError';
    this.problems = problems;
  }
}

const OPS = { '==': (a, b) => a === b, '!=': (a, b) => a !== b, '>=': (a, b) => a >= b, '<=': (a, b) => a <= b, '>': (a, b) => a > b, '<': (a, b) => a < b, in: (a, b) => Array.isArray(b) && b.includes(a) };

/** Check a rules file. Returns `[]` when clean, otherwise a list of `{ where, problem }`. */
export function lintRules(file) {
  const problems = [];
  const texts = (obj, where) => {
    for (const [loc, s] of Object.entries(obj ?? {})) {
      if (typeof s !== 'string') { problems.push({ where: `${where}.${loc}`, problem: 'not a string' }); continue; }
      for (const re of FORBIDDEN) if (re.test(s)) problems.push({ where: `${where}.${loc}`, problem: `forbidden term ${re}` });
    }
  };
  const ids = new Set();
  for (const r of file.rules ?? []) {
    const where = `rule ${r.id ?? '(no id)'}`;
    if (!r.id) problems.push({ where, problem: 'missing id' });
    if (ids.has(r.id)) problems.push({ where, problem: 'duplicate id' });
    ids.add(r.id);
    if (!r.feature) problems.push({ where, problem: 'missing feature' });
    if (!OPS[r.op]) problems.push({ where, problem: `unknown op ${r.op}` });
    if (!TIERS.includes(r.tier)) problems.push({ where, problem: `tier must be one of ${TIERS.join(', ')}` });
    if (!r.source || typeof r.source !== 'string') problems.push({ where, problem: 'missing source' });
    if (!file.readings?.[r.reading]) problems.push({ where, problem: `no reading "${r.reading}"` });
    for (const k of ['id', 'feature', 'source']) for (const re of FORBIDDEN) if (typeof r[k] === 'string' && re.test(r[k])) problems.push({ where: `${where}.${k}`, problem: `forbidden term ${re}` });
  }
  for (const [key, rd] of Object.entries(file.readings ?? {})) {
    const where = `reading ${key}`;
    if (!VALENCES.includes(rd.valence)) problems.push({ where, problem: `valence must be one of ${VALENCES.join(', ')}` });
    if (!rd.text?.en) problems.push({ where, problem: 'missing English text' });
    texts(rd.text, `${where}.text`);
    if (rd.valence === 'mixed' && !rd.guidance?.en) problems.push({ where, problem: 'a mixed reading must carry guidance' });
    texts(rd.guidance, `${where}.guidance`);
  }
  // Site-wide linter uses the open product profile (fortune / medical / death allowed).
  for (const h of lintObject({ rules: file.rules, readings: file.readings }, 'open')) problems.push({ where: h.path, problem: `forbidden term "${h.term}" (site-wide linter)` });
  return problems;
}

/** Validate and return a rules file; throws {@link RuleVocabularyError} if it is not clean. */
export function loadRules(file = rulesFile) {
  const problems = lintRules(file);
  if (problems.length) throw new RuleVocabularyError(problems);
  return file;
}

/** The detection-confidence group a feature belongs to: `line.heart.origin` → `line.heart`. */
export function confidenceGroup(feature) {
  const p = feature.split('.');
  if (p[0] === 'line' || p[0] === 'mount') return `${p[0]}.${p[1]}`;
  if (p[0] === 'lines') return 'lines.headLife';
  return p[0];
}

/**
 * Interpret features.
 *
 * `features` is `{ values: { 'line.heart.origin': 'jupiter', … }, confidence: { 'line.heart': 0.7, … } }`.
 * Returns `{ readings, suppressed, notDetected, locale, boundaries }`.
 *
 * A reading is `{ id, reading, text, guidance?, valence, tier, source, trigger: { feature, value }, confidence }`.
 */
export function read(features, { locale = 'en', rules = null } = {}) {
  const file = loadRules(rules ?? rulesFile);
  const readings = []; const suppressed = [];
  for (const r of file.rules) {
    if (!(r.feature in features.values)) continue;
    const value = features.values[r.feature];
    if (!OPS[r.op](value, r.value)) continue;
    const conf = features.confidence?.[confidenceGroup(r.feature)] ?? 0;
    const floor = r.floor ?? DEFAULT_FLOOR;
    if (conf < floor) { suppressed.push({ id: r.id, feature: r.feature, confidence: conf, floor }); continue; }
    const rd = file.readings[r.reading];
    readings.push({
      id: r.id, reading: r.reading, valence: rd.valence, tier: r.tier, source: r.source,
      text: rd.text[locale] ?? rd.text.en, guidance: rd.guidance ? (rd.guidance[locale] ?? rd.guidance.en) : null,
      trigger: { feature: r.feature, value }, confidence: conf, textLocale: rd.text[locale] ? locale : 'en'
    });
  }
  const notDetected = Object.keys(features.notDetected ?? {}).filter((k) => features.notDetected[k]);
  return { readings, suppressed, notDetected, locale, boundaries: BOUNDARIES };
}

/** The standing boundary statements, shown with every reading (i18n keys `palm.boundary.*`). */
export const BOUNDARIES = Object.freeze(['descriptive', 'medical-allowed', 'lifespan-allowed', 'photo-limits']);

export function lineReading(features, line, opts) { return read(features, opts).readings.filter((r) => r.trigger.feature.split('.')[1] === line); }
export function mountReading(features, opts) { return read(features, opts).readings.filter((r) => r.trigger.feature.startsWith('mount.')); }
export function handTypeReading(features, opts) { return read(features, opts).readings.filter((r) => r.trigger.feature === 'hand.type'); }
